import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Loader2,
  Heart,
  Sparkles,
  MapPin,
  Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import { formatLargeNumber } from '../lib/utils';
import { useTileSize } from '../hooks/useTileSize';
import { TileSizeSlider } from '../components/TileSizeSlider';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (typeof url === 'object' && url.url) return url.url;
  if (typeof url !== 'string') return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_BASE_URL}${url}`;
  return url;
};

export default function WantMatchesPage() {
  const { wantId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { tileSize, setTileSize, gridStyle, MIN_TILE_SIZE, MAX_TILE_SIZE } = useTileSize();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchMatches();
  }, [wantId, page]);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/wtb/${wantId}/matches?page=${page}&limit=24`);
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches');
      navigate('/my-wants');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchClick = (matchId) => {
    navigate(`/matches/${matchId}`);
  };

  if (loading && !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/my-wants')}
            className="text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to My Wants
          </Button>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
              Matches for "{data?.wtb_title}"
            </h1>
            <p className="text-[var(--theme-text-secondary)]">
              {formatLargeNumber(data?.total_count || 0)} items match your criteria
            </p>
          </div>
          <TileSizeSlider tileSize={tileSize} setTileSize={setTileSize} min={MIN_TILE_SIZE} max={MAX_TILE_SIZE} />
        </div>

        {/* Matches Grid */}
        {data?.matches?.length > 0 ? (
          <>
            <div style={gridStyle}>
              {data.matches.map((match) => (
                <MatchItemCard
                  key={match.match_id}
                  match={match}
                  onClick={() => handleMatchClick(match.match_id)}
                />
              ))}
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-[var(--theme-text-secondary)]">
                  Page {page} of {data.total_pages}
                </span>
                <Button
                  variant="outline"
                  disabled={page >= data.total_pages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-[var(--theme-hover)] flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-[var(--theme-text-muted)]" />
            </div>
            <h3 className="text-lg font-semibold text-[var(--theme-text-primary)] mb-2">
              No matches yet
            </h3>
            <p className="text-[var(--theme-text-secondary)] max-w-md mx-auto">
              We're actively searching for items that match your criteria. Check back soon!
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}

// Match Item Card Component (catalog style)
function MatchItemCard({ match, onClick }) {
  const [imgError, setImgError] = useState(false);
  const item = match.item;
  
  const imageUrl = item?.images?.[0] 
    ? normalizeImageUrl(typeof item.images[0] === 'object' ? item.images[0].url : item.images[0])
    : null;

  return (
    <div
      onClick={onClick}
      className="bg-[var(--theme-card)] rounded-xl border border-[var(--theme-border)] hover:border-[var(--theme-border-hover)] overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg group"
      data-testid={`match-card-${match.match_id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-[var(--theme-hover)]">
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={item?.title}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-[var(--theme-text-muted)]" />
          </div>
        )}
        
        {/* Status badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {match.has_special_interest && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#C9956C]/90 text-white flex items-center gap-1">
              <Heart className="w-3 h-3" />
              INTERESTED
            </span>
          )}
        </div>
        
        {/* Match score */}
        {match.match_score > 0 && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/60 text-white flex items-center gap-1">
            <Star className="w-3 h-3 text-[#C9956C]" />
            {Math.round(match.match_score * 100)}%
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-semibold text-sm text-[var(--theme-text-primary)] line-clamp-2 group-hover:text-[#1E3A5F] transition-colors">
          {item?.title || 'Item'}
        </h3>
        <p className="text-base font-bold text-[#059669] mt-1">
          ${item?.price?.toLocaleString() || '0'}
        </p>
        {item?.location_name && (
          <p className="text-xs text-[var(--theme-text-muted)] mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{item.location_name}</span>
          </p>
        )}
      </div>
    </div>
  );
}
